/**
 * "Questions for your team" on the document overview (R1 Q2; draft page
 * redesign): every question the AI raised about this document, and the only
 * place they are answered — the AI panel points here instead of repeating
 * them.
 *
 * One bordered card, a row per question in the order asked (a question keeps
 * its number when it is answered), "X of N answered" with a progress bar.
 * One row is open at a time: the first waiting question, until the person
 * picks another. Hidden when the document has no questions.
 *
 * Reached by a link to `#document-questions` (the tray, the panel from
 * another page) and by "Answer questions" on this page (`requestFocus`):
 * both bring the list into view with the first waiting question open.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import type { QuestionAsk } from '@/lib/api'
import {
  DOCUMENT_QUESTIONS_ANCHOR,
  isWaitingQuestion,
  questionProgress,
  questionsInAskedOrder,
} from '@/lib/question-view'
import { QUESTIONS_ANSWERED_COUNT, QUESTIONS_HEADING, fillCopy } from '@/lib/question-copy'
import { selectDocumentQuestions, useQuestionsStore } from '@/stores/questions-store'
import { QuestionBlock } from '@/components/drafting/QuestionBlock'

interface DocumentQuestionsListProps {
  bidId: string
  artifactId: string
  /** Section headings by section key, to name where a waiting question's answer goes. */
  sectionHeadings?: Readonly<Record<string, string>>
  className?: string
}

export function DocumentQuestionsList({ bidId, artifactId, sectionHeadings, className }: DocumentQuestionsListProps) {
  const load = useQuestionsStore((s) => s.load)
  const asks = useQuestionsStore((s) => selectDocumentQuestions(s, artifactId).asks)
  const focusRequest = useQuestionsStore((s) => s.focusRequest)
  const { hash } = useLocation()
  const sectionRef = useRef<HTMLElement>(null)
  const hasScrolled = useRef(false)
  // A request made before this list mounted is not for it.
  const handledFocusSeq = useRef(focusRequest?.seq ?? 0)

  const ordered = useMemo(() => questionsInAskedOrder(asks), [asks])
  const firstWaitingId = ordered.find(isWaitingQuestion)?.id ?? null
  // undefined: the person has not picked a row yet, so the first waiting one is open.
  const [pickedId, setPickedId] = useState<string | null | undefined>(undefined)
  const openId = pickedId === undefined ? firstWaitingId : pickedId

  useEffect(() => {
    if (bidId && artifactId) void load(bidId, artifactId)
  }, [bidId, artifactId, load])

  // Pin the first waiting question once the list has loaded, so answering it
  // does not move the open row to the next one.
  const hasQuestions = ordered.length > 0
  useEffect(() => {
    if (pickedId === undefined && hasQuestions) setPickedId(firstWaitingId)
  }, [pickedId, hasQuestions, firstWaitingId])

  // Once the open question is answered or skipped (it stops being 'open'), open
  // the next unanswered one: an 'open' question after it, else any 'open' one,
  // else a skipped one. Editing an answered question never jumps.
  const openAsk = ordered.find((ask) => ask.id === openId)
  const openIsUnanswered = openAsk?.question.status === 'open'
  const lastOpen = useRef<{ id: string | null; unanswered: boolean }>({ id: null, unanswered: false })
  useEffect(() => {
    const previous = lastOpen.current
    lastOpen.current = { id: openId, unanswered: openIsUnanswered }
    if (!openId || previous.id !== openId || !previous.unanswered || openIsUnanswered) return
    const index = ordered.findIndex((ask) => ask.id === openId)
    const isOpenStatus = (ask: QuestionAsk) => ask.question.status === 'open'
    const next =
      ordered.slice(index + 1).find(isOpenStatus) ??
      ordered.find(isOpenStatus) ??
      ordered.find((ask) => ask.id !== openId && isWaitingQuestion(ask))
    if (next) setPickedId(next.id)
  }, [openId, openIsUnanswered, ordered])

  // A link to `#document-questions` lands on the list once it has rendered.
  const isTargeted = hash === `#${DOCUMENT_QUESTIONS_ANCHOR}`
  useEffect(() => {
    if (!isTargeted || !hasQuestions || hasScrolled.current) return
    hasScrolled.current = true
    sectionRef.current?.scrollIntoView?.({ block: 'start' })
  }, [isTargeted, hasQuestions])

  // "Answer questions" on this page: into view, the first waiting question open.
  useEffect(() => {
    if (!focusRequest || focusRequest.artifactId !== artifactId || focusRequest.seq === handledFocusSeq.current) return
    handledFocusSeq.current = focusRequest.seq
    if (firstWaitingId) setPickedId(firstWaitingId)
    sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [focusRequest, artifactId, firstWaitingId])

  if (!hasQuestions) return null
  const { answered, total } = questionProgress(ordered)
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0
  const answeredLine = fillCopy(QUESTIONS_ANSWERED_COUNT, { answered, total })

  const headingFor = (ask: QuestionAsk): string | null => {
    if (!sectionHeadings) return null
    for (const target of ask.question.targets) {
      if (target.artifactId !== artifactId) continue
      const heading = sectionHeadings[target.sectionKey]
      if (heading) return heading
    }
    return null
  }

  return (
    <section
      ref={sectionRef}
      id={DOCUMENT_QUESTIONS_ANCHOR}
      aria-label={QUESTIONS_HEADING}
      className={cn('scroll-mt-20', className)}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-foreground">{QUESTIONS_HEADING}</h3>
        <div className="flex items-center gap-2">
          <span className="text-meta" data-testid="questions-answered-count">{answeredLine}</span>
          <div
            role="progressbar"
            aria-label={answeredLine}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={answered}
            className="h-1.5 w-[120px] overflow-hidden rounded-full bg-muted"
          >
            <div className="h-full rounded-full bg-[hsl(var(--status-success))]" style={{ width: `${percent}%` }} />
          </div>
        </div>
      </div>
      <ul className="mt-2 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        {ordered.map((ask, index) => (
          <QuestionBlock
            key={ask.id}
            bidId={bidId}
            ask={ask}
            number={index + 1}
            isOpen={openId === ask.id}
            onToggle={() => setPickedId(openId === ask.id ? null : ask.id)}
            sectionHeading={headingFor(ask)}
          />
        ))}
      </ul>
    </section>
  )
}
